<div class="modal-content">
  <div class="modal-header">
    <div class="d-flex flex-column">
      <h2 class="modal-title mb-1">{{ 'companyProfile.viewInvoiceModal.title' | translate }} #{{ order.order_id }}</h2>
      <div class="d-flex align-items-center gap-2">
        <span class="badge {{ getStatusClass(order.status) }}">{{ order.status | titlecase }}</span>
        <span class="text-muted">{{ getPaymentTypeLabel(order.payment_type) }}</span>
      </div>
    </div>
    <i class="fa-solid fa-xmark fs-2 cursor-pointer" (click)="close()"></i>
  </div>

  <div class="modal-body">
    <!-- Customer Info -->
    <div class="info-section mb-4">
      <h5 class="section-title">
        <i class="fa fa-user me-2 text-primary"></i>
        {{ 'companyProfile.viewInvoiceModal.customer' | translate }}
      </h5>
      <div class="info-card">
        @if (order.user) {
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.name' | translate }}</span>
            <span class="info-value">{{ order.user.first_name }} {{ order.user.last_name }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.email' | translate }}</span>
            <span class="info-value">{{ order.user.email }}</span>
          </div>
          @if (order.user.phone) {
            <div class="info-row">
              <span class="info-label">{{ 'companyProfile.viewInvoiceModal.phone' | translate }}</span>
              <span class="info-value">{{ order.user.phone }}</span>
            </div>
          }
        } @else {
          <span class="text-muted">{{ 'companyProfile.viewInvoiceModal.noCustomerInfo' | translate }}</span>
        }
      </div>
    </div>

    <!-- Dates -->
    <div class="info-section mb-4">
      <h5 class="section-title">
        <i class="fa fa-calendar me-2 text-primary"></i>
        {{ 'companyProfile.viewInvoiceModal.dates' | translate }}
      </h5>
      <div class="info-card">
        <div class="info-row">
          <span class="info-label">{{ 'companyProfile.viewInvoiceModal.created' | translate }}</span>
          <span class="info-value">{{ order.created_at | date:'medium' }}</span>
        </div>
        @if (order.due_date) {
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.dueDate' | translate }}</span>
            <span class="info-value" [class.text-danger]="order.status === 'pending'">{{ order.due_date | date:'mediumDate' }}</span>
          </div>
        }
        @if (order.paid_at) {
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.paid' | translate }}</span>
            <span class="info-value text-success">{{ order.paid_at | date:'medium' }}</span>
          </div>
        }
        @if (order.payment_type === 'subscription' && order.next_billing_date) {
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.nextBilling' | translate }}</span>
            <span class="info-value">{{ order.next_billing_date | date:'mediumDate' }}</span>
          </div>
        }
      </div>
    </div>

    <!-- Subscription Status -->
    @if (order.payment_type === 'subscription' && order.subscription_status) {
      <div class="info-section mb-4">
        <h5 class="section-title">
          <i class="fa fa-sync-alt me-2 text-primary"></i>
          {{ 'companyProfile.viewInvoiceModal.subscription' | translate }}
        </h5>
        <div class="info-card">
          <div class="info-row">
            <span class="info-label">{{ 'companyProfile.viewInvoiceModal.status' | translate }}</span>
            <span class="info-value">
              <span class="badge" [class.badge-success]="order.subscription_status === 'active'" [class.badge-warning]="order.subscription_status === 'past_due'" [class.badge-secondary]="order.subscription_status === 'cancelled'">
                {{ order.subscription_status | titlecase }}
              </span>
            </span>
          </div>
          @if (order.stripe_subscription_id) {
            <div class="info-row">
              <span class="info-label">{{ 'companyProfile.viewInvoiceModal.stripeId' | translate }}</span>
              <span class="info-value text-muted fs-7">{{ order.stripe_subscription_id }}</span>
            </div>
          }
        </div>
      </div>
    }

    <!-- Line Items -->
    <div class="info-section mb-4">
      <h5 class="section-title">
        <i class="fa fa-list me-2 text-primary"></i>
        {{ 'companyProfile.viewInvoiceModal.items' | translate }} ({{ order.order_lines?.length || 0 }})
      </h5>
      <div class="items-table">
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>{{ 'companyProfile.viewInvoiceModal.description' | translate }}</th>
              <th class="text-center">{{ 'companyProfile.viewInvoiceModal.qty' | translate }}</th>
              <th class="text-end">{{ 'companyProfile.viewInvoiceModal.unitPrice' | translate }}</th>
              <th class="text-end">{{ 'companyProfile.viewInvoiceModal.vat' | translate }}</th>
              <th class="text-end">{{ 'companyProfile.viewInvoiceModal.total' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (line of order.order_lines; track line.line_id) {
              <tr>
                <td>
                  {{ line.description }}
                  @if (line.service) {
                    <br><small class="text-muted">{{ 'companyProfile.viewInvoiceModal.service' | translate }}: {{ line.service.service_name }}</small>
                  }
                </td>
                <td class="text-center">{{ line.quantity }}</td>
                <td class="text-end">{{ formatPrice(line.unit_price_cents) }}</td>
                <td class="text-end">
                  @if (line.vat_rate > 0) {
                    {{ line.vat_rate }}%
                  } @else {
                    -
                  }
                </td>
                <td class="text-end fw-bold">{{ formatPrice(line.total_cents + (line.vat_cents || 0)) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <!-- Totals -->
    <div class="totals-section">
      <div class="totals-row">
        <span>{{ 'companyProfile.viewInvoiceModal.subtotal' | translate }}</span>
        <span>{{ formatPrice(getSubtotal()) }} {{ order.currency }}</span>
      </div>
      <div class="totals-row">
        <span>{{ 'companyProfile.viewInvoiceModal.vat' | translate }}</span>
        <span>{{ formatPrice(getTotalVat()) }} {{ order.currency }}</span>
      </div>
      <div class="totals-row total">
        <span>{{ 'companyProfile.viewInvoiceModal.totalAmount' | translate }}</span>
        <span>{{ formatPrice(order.total_cents) }} {{ order.currency }}</span>
      </div>
      <div class="totals-row platform-fee">
        <span>{{ 'companyProfile.viewInvoiceModal.platformFee' | translate }}</span>
        <span class="text-muted">-{{ formatPrice(order.platform_fee_cents) }} {{ order.currency }}</span>
      </div>
      <div class="totals-row net">
        <span>{{ 'companyProfile.viewInvoiceModal.netAmount' | translate }}</span>
        <span class="text-success">{{ formatPrice(order.total_cents - order.platform_fee_cents) }} {{ order.currency }}</span>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <app-button
      [variant]="'neutral'"
      [label]="'common.close' | translate"
      (clicked)="close()"
    />
  </div>
</div>
